/* eslint-disable react-hooks/rules-of-hooks */
/**
 * AlWidget - App-wide AI Assistant floating widget (named by ASSISTANT_NAME, formerly "Al")
 *
 * Independently draggable — position saved to localStorage.
 * Click the button to open/close. Drag to reposition.
 * Mobile: opens full-screen automatically.
 *
 * Features:
 * - Route-aware: knows current + previous screen for context
 * - Conversation persists across navigation (no reset on route change)
 * - Module-specific suggested prompts based on current route
 * - Fullscreen mode with left drawer showing conversation history
 * - Independent drag (not tied to feedback button position)
 * - Touch support for mobile dragging
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Minus,
  X,
  RotateCcw,
  Maximize2,
  Minimize2,
  Wand2,
  Plus,
  MessageSquare,
  Clock,
} from 'lucide-react';
import { AlChatPanel } from './AlChatPanel';
import { ASSISTANT_NAME } from './assistantName';
import { useAuth } from '../../contexts/AuthContext';
import { useConversations } from '../../hooks/crm/useAISalesChat';
import type { Conversation } from '../../types/ai-sales-chat';

// ============================================================================
// TYPES
// ============================================================================

interface Position {
  readonly x: number;
  readonly y: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 520;
const BUTTON_STORAGE_KEY = 'oh_al_button_pos';
const PANEL_OFFSET_STORAGE_KEY = 'oh_al_panel_offset';
const BUTTON_SIZE = 56;
const PANEL_GAP = 12;
const VIEWPORT_PAD = 8;
const DRAG_THRESHOLD = 5; // px movement before treating as drag vs click

// ============================================================================
// HELPERS
// ============================================================================

function getDefaultButtonPos() {
  return {
    bottom: typeof window !== 'undefined' ? Math.round(window.innerHeight / 2) : 300,
    right: 24,
  };
}

function loadPos(key: string, fallback: { bottom: number; right: number }) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

function isMobileScreen() {
  return typeof window !== 'undefined' && window.innerWidth < 640;
}

function getPointerXY(e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent) {
  if ('touches' in e && e.touches.length > 0) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  if ('clientX' in e) {
    return { x: e.clientX, y: e.clientY };
  }
  return { x: 0, y: 0 };
}

// ============================================================================
// ROUTE CONTEXT HOOK
// ============================================================================

function useRouteContext() {
  const location = useLocation();
  const prevRouteRef = useRef(location.pathname);
  const [previousRoute, setPreviousRoute] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (location.pathname !== prevRouteRef.current) {
      setPreviousRoute(prevRouteRef.current);
      prevRouteRef.current = location.pathname;
    }
  }, [location.pathname]);

  return {
    currentRoute: location.pathname,
    previousRoute,
  };
}

function getModuleFromRoute(route: string): string {
  if (route.startsWith('/crm')) return 'sales';
  if (route.startsWith('/support')) return 'support';
  if (route.startsWith('/projects')) return 'projects';
  if (route.startsWith('/quoting')) return 'quoting';
  if (route.startsWith('/billing')) return 'billing';
  if (route.startsWith('/assets') || route.startsWith('/fleet')) return 'assets';
  if (route.startsWith('/forms')) return 'forms';
  if (route.startsWith('/field-service')) return 'field-service';
  if (route.startsWith('/hr')) return 'hr';
  if (route.startsWith('/insurance')) return 'insurance';
  if (route.startsWith('/marketing')) return 'marketing';
  if (route.startsWith('/admin')) return 'admin';
  if (route.startsWith('/command')) return 'command';
  if (route.startsWith('/technician')) return 'technician';
  return 'general';
}

// ============================================================================
// CONVERSATION HISTORY DRAWER (fullscreen only)
// ============================================================================

function formatRelativeTime(conversation: Conversation): string {
  if (!conversation.lastMessageAt) return '';
  const date = conversation.lastMessageAt.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface ConversationDrawerProps {
  conversations: Conversation[];
  activeConversationId?: string;
  onSelect: (id: string) => void;
  onNew: () => void;
  onClose?: () => void;
  className?: string;
}

function ConversationDrawer({
  conversations,
  activeConversationId,
  onSelect,
  onNew,
  onClose,
  className = 'w-60 flex-shrink-0',
}: ConversationDrawerProps) {
  return (
    <div className={`border-r border-slate-200 flex flex-col bg-slate-50 ${className}`}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-300 bg-white">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">History</span>
        <div className="flex items-center gap-1">
          <button
            onClick={onNew}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors"
            title="New conversation"
          >
            <Plus className="w-3 h-3" />
            New
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2.5 rounded hover:bg-slate-100 transition-colors"
              title="Close history"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-400">
            <Clock className="w-5 h-5" />
            <span className="text-xs">No conversations yet</span>
          </div>
        ) : (
          <div className="py-1">
            {conversations.map((conv) => (
              <button
                key={conv.conversationId}
                onClick={() => onSelect(conv.conversationId)}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-100 transition-colors border-b border-slate-100 last:border-0 ${
                  activeConversationId === conv.conversationId
                    ? 'bg-indigo-50 border-l-2 border-l-indigo-500'
                    : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate leading-tight">
                      {conv.title || 'Untitled conversation'}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {formatRelativeTime(conv)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AlWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isMobile, setIsMobile] = useState(isMobileScreen);
  const [showMobileHistory, setShowMobileHistory] = useState(false);

  // Button position — independently draggable, saved to localStorage
  const [buttonPos, setButtonPos] = useState(() => loadPos(BUTTON_STORAGE_KEY, getDefaultButtonPos()));

  // Panel offset relative to button — saved separately
  const [panelOffset, setPanelOffset] = useState<Position>(() => {
    const saved = loadPos(PANEL_OFFSET_STORAGE_KEY, { bottom: 0, right: 0 });
    return { x: saved.right ?? 0, y: saved.bottom ?? 0 };
  });

  const { user } = useAuth();
  const { currentRoute, previousRoute } = useRouteContext();
  const currentModule = getModuleFromRoute(currentRoute);
  const { data: conversations = [] } = useConversations({ isArchived: false });

  const alEnabled = user?.preferences?.alPreferences?.enabled !== false;

  // Button drag state
  const btnIsDragging = useRef(false);
  const btnHasMoved = useRef(false);
  const btnDragStart = useRef({ x: 0, y: 0 });
  const btnPosStart = useRef({ bottom: 0, right: 24 });

  // Panel header drag state
  const panelIsDragging = useRef(false);
  const panelDragStart = useRef({ x: 0, y: 0 });
  const panelOffsetStart = useRef<Position>({ x: 0, y: 0 });

  // Track mobile on resize
  useEffect(() => {
    const handler = () => setIsMobile(isMobileScreen());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Reset mobile history drawer when leaving mobile viewport
  useEffect(() => {
    if (!isMobile) setShowMobileHistory(false);
  }, [isMobile]);

  // ---- Button drag (mouse + touch) ----
  const handleButtonPointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const { x, y } = getPointerXY(e);
      btnIsDragging.current = true;
      btnHasMoved.current = false;
      btnDragStart.current = { x, y };
      btnPosStart.current = { ...buttonPos };
      // Don't preventDefault here — allows click events through if no drag occurs
    },
    [buttonPos]
  );

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!btnIsDragging.current) return;
      const { x, y } = getPointerXY(e);
      const dx = x - btnDragStart.current.x;
      const dy = y - btnDragStart.current.y;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        btnHasMoved.current = true;
        if ('touches' in e) e.preventDefault(); // prevent scroll on touch drag
      }
      if (btnHasMoved.current) {
        setButtonPos({
          right: Math.max(0, btnPosStart.current.right - dx),
          bottom: Math.max(0, btnPosStart.current.bottom - dy),
        });
      }
    };
    const onUp = () => {
      if (btnIsDragging.current) {
        btnIsDragging.current = false;
        if (btnHasMoved.current) {
          setButtonPos((pos: { bottom: number; right: number }) => {
            localStorage.setItem(BUTTON_STORAGE_KEY, JSON.stringify(pos));
            return pos;
          });
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  // ---- Panel header drag (mouse + touch, desktop only) ----
  const handlePanelHeaderPointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      if (isMobile) return; // no panel drag on mobile (it's fullscreen)
      const { x, y } = getPointerXY(e);
      panelIsDragging.current = true;
      panelDragStart.current = { x, y };
      panelOffsetStart.current = { ...panelOffset };
      e.preventDefault();
    },
    [panelOffset, isMobile]
  );

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!panelIsDragging.current) return;
      const { x, y } = getPointerXY(e);
      const dx = x - panelDragStart.current.x;
      const dy = y - panelDragStart.current.y;
      const newOffset = {
        x: panelOffsetStart.current.x + dx,
        y: panelOffsetStart.current.y + dy,
      };
      setPanelOffset(newOffset);
    };
    const onUp = () => {
      if (panelIsDragging.current) {
        panelIsDragging.current = false;
        setPanelOffset((offset: Position) => {
          localStorage.setItem(PANEL_OFFSET_STORAGE_KEY, JSON.stringify({ right: offset.x, bottom: offset.y }));
          return offset;
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  if (!alEnabled) return null;

  const handleToggle = () => {
    // Only fire click if button wasn't dragged
    if (btnHasMoved.current) return;
    setIsOpen((prev) => !prev);
  };

  const handleMinimize = () => setIsOpen(false);

  const handleClose = () => {
    setIsOpen(false);
    setIsFullscreen(false);
    setPanelOffset({ x: 0, y: 0 });
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen((prev) => !prev);
    setPanelOffset({ x: 0, y: 0 });
  };

  const handleNewConversation = () => setConversationId(undefined);

  // Compute panel position (desktop only — mobile is fullscreen)
  const rawBottom = buttonPos.bottom + BUTTON_SIZE + PANEL_GAP + panelOffset.y;
  const rawRight = buttonPos.right + panelOffset.x * -1;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  const maxBottom = vh - Math.min(PANEL_HEIGHT, vh - VIEWPORT_PAD * 2) - VIEWPORT_PAD;
  const maxRight = vw - PANEL_WIDTH - VIEWPORT_PAD;

  const panelBottom = Math.max(VIEWPORT_PAD, Math.min(rawBottom, maxBottom));
  const panelRight = Math.max(VIEWPORT_PAD, Math.min(rawRight, maxRight));

  // On mobile open → always full-screen, no panel drag
  const showFullscreen = isFullscreen || (isOpen && isMobile);

  return (
    <>
      {/* Trigger button */}
      <button
        onMouseDown={handleButtonPointerDown}
        onTouchStart={handleButtonPointerDown}
        onClick={handleToggle}
        className={`fixed ${isOpen ? 'z-[60]' : 'z-40'} rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-grab active:cursor-grabbing`}
        style={{
          background: isOpen
            ? 'linear-gradient(135deg, #3730a3, #6d28d9)'
            : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          bottom: buttonPos.bottom,
          right: buttonPos.right,
        }}
        title={`${ASSISTANT_NAME} — AI Assistant`}
        aria-label={isOpen ? `Close ${ASSISTANT_NAME}` : `Open ${ASSISTANT_NAME}`}
      >
        <Wand2 className="h-6 w-6 text-white" strokeWidth={1.5} />
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          // Stop propagation so clicks inside the panel don't bubble to
          // any parent drag handlers
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className={`fixed z-50 flex overflow-hidden ${
            showFullscreen
              ? 'inset-0 rounded-none flex-row'
              : 'flex-col rounded-xl shadow-2xl border border-slate-200 bg-white'
          }`}
          style={showFullscreen ? undefined : {
            width: Math.min(PANEL_WIDTH, vw - VIEWPORT_PAD * 2),
            height: Math.min(PANEL_HEIGHT, vh - VIEWPORT_PAD * 2),
            bottom: panelBottom,
            right: panelRight,
            maxHeight: `calc(100vh - ${VIEWPORT_PAD * 2}px)`,
            maxWidth: `calc(100vw - ${VIEWPORT_PAD * 2}px)`,
          }}
        >
          {/* Fullscreen: left conversation history drawer (desktop only — mobile is too narrow for the side-by-side layout) */}
          {showFullscreen && !isMobile && (
            <ConversationDrawer
              conversations={conversations}
              activeConversationId={conversationId}
              onSelect={setConversationId}
              onNew={handleNewConversation}
            />
          )}

          {/* Main panel content — flex-1 so it always fills the panel height */}
          <div className={`relative flex flex-col bg-white flex-1 min-w-0 min-h-0`}>
            {/* Header — drag handle on desktop, just controls on mobile */}
            <div
              onMouseDown={showFullscreen ? undefined : handlePanelHeaderPointerDown}
              onTouchStart={showFullscreen ? undefined : handlePanelHeaderPointerDown}
              className={`flex items-center justify-between px-4 bg-gradient-to-r from-indigo-600 to-purple-600 select-none flex-shrink-0 ${
                isMobile ? 'py-3' : 'py-2.5'
              } ${showFullscreen ? '' : 'cursor-grab active:cursor-grabbing'}`}
            >
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-white/80" strokeWidth={1.5} />
                <span className="text-sm font-semibold text-white">{ASSISTANT_NAME}</span>
                <span className="text-xs text-white/50 font-normal">
                  {currentModule !== 'general' ? `· ${currentModule}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {/* Mobile: history toggle (drawer is hidden on mobile to leave room for chat) */}
                {showFullscreen && isMobile && (
                  <button
                    onClick={() => setShowMobileHistory(true)}
                    className="rounded hover:bg-white/20 transition-colors p-2"
                    title="Conversation history"
                  >
                    <Clock className="w-4 h-4 text-white" />
                  </button>
                )}
                <button
                  onClick={handleNewConversation}
                  className={`rounded hover:bg-white/20 transition-colors ${isMobile ? 'p-2' : 'p-1'}`}
                  title="New conversation"
                >
                  <RotateCcw className={isMobile ? 'w-4 h-4 text-white' : 'w-3.5 h-3.5 text-white'} />
                </button>
                {!isMobile && (
                  <button
                    onClick={handleFullscreenToggle}
                    className="p-1 rounded hover:bg-white/20 transition-colors"
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? (
                      <Minimize2 className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <Maximize2 className="w-3.5 h-3.5 text-white" />
                    )}
                  </button>
                )}
                {!isMobile && (
                  <button
                    onClick={handleMinimize}
                    className="p-1 rounded hover:bg-white/20 transition-colors"
                    title="Minimize"
                  >
                    <Minus className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
                {/* On mobile this is the primary close — larger touch target */}
                <button
                  onClick={handleClose}
                  className={`rounded hover:bg-white/20 transition-colors ${isMobile ? 'p-2' : 'p-1'}`}
                  title="Close"
                >
                  <X className={isMobile ? 'w-5 h-5 text-white' : 'w-3.5 h-3.5 text-white'} />
                </button>
              </div>
            </div>

            {/* Chat Panel — clicks here won't trigger any parent drag logic */}
            <div className="flex-1 min-h-0">
              <AlChatPanel
                className="h-full"
                conversationId={conversationId}
                onConversationCreated={setConversationId}
                routeContext={{
                  currentRoute,
                  previousRoute,
                  currentModule,
                }}
              />
            </div>

            {/* Mobile slide-over history overlay (drawer is hidden on mobile fullscreen) */}
            {showFullscreen && isMobile && showMobileHistory && (
              <div className="absolute inset-0 z-10 flex">
                <ConversationDrawer
                  conversations={conversations}
                  activeConversationId={conversationId}
                  onSelect={(id) => {
                    setConversationId(id);
                    setShowMobileHistory(false);
                  }}
                  onNew={() => {
                    handleNewConversation();
                    setShowMobileHistory(false);
                  }}
                  onClose={() => setShowMobileHistory(false)}
                  className="w-3/4 max-w-xs h-full"
                />
                {/* Backdrop tap closes the drawer */}
                <button
                  type="button"
                  onClick={() => setShowMobileHistory(false)}
                  className="flex-1 bg-black/30"
                  aria-label="Close history"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default AlWidget;
